//dependencies
import React, { ReactNode } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

import { TextSize } from "../../Text";
//components
import InputWrapper from "../InputWrapper";
//styles
import css from "./InputTextArea.module.scss";

type InputTextAreaProps = {
	/**
	 * The value of the input
	 */
	value: string;
	/**
	 * The placeholder for the input
	 */
	placeholder?: string;
	/**
	 * The label for the input
	 */
	label?: string;
	/**
	 * The size of the label
	 */
	labelSize?: TextSize;
	/**
	 * Optional subtitle for the input
	 */
	subtitle?: string | ReactNode;
	/**
	 * The class name to apply to the input
	 */
	className?: string;
	/**
	 * The callback for when the value changes
	 */
	onChange: (value: string) => void;
	/**
	 * The callback for when a key is pressed
	 */
	onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;

	/**
	 * Whether or not the input is disabled
	 */
	disabled?: boolean;

	/**
	 * The id of the input. If not provided, a random id will be generated
	 * by nanoid.
	 * @default undefined
	 */
	testId?: string;
	/**
	 * The minimum and maximum number of rows to display
	 */
	minRows?: number;
	/**
	 * The maximum and maximum number of rows to display
	 */
	maxRows?: number;

	/**
	 * The error message to display
	 */
	errorMessage?: string;

	/**
	 * Whether or not the input is errored
	 */
	errored?: boolean;

	/**
	 * The id of the input
	 */
	id?: string;
};

/**
 * A controlled textarea input component
 */
const InputTextArea = ({
	id,
	value,
	disabled,
	placeholder,
	label,
	subtitle = null,
	onChange,
	labelSize,
	className,
	onKeyDown,
	testId,
	minRows = 1,
	maxRows,
	errorMessage,
	errored = false,
}: InputTextAreaProps) => {
	return (
		<InputWrapper label={label} labelSize={labelSize} errorMessage={errorMessage} animatedError>
			{subtitle}
			<ReactTextareaAutosize
				id={id}
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				className={`${css.input} ${errorMessage || errored ? css.error : ""} ${className}`}
				onKeyDown={onKeyDown}
				data-testid={testId}
				minRows={minRows}
				maxRows={maxRows}
				disabled={disabled}
			/>
		</InputWrapper>
	);
};

export default InputTextArea;
